/**
 * Funnel chart — Reached → RSVP-yes → Attended, with walk-in entering
 * as a side flow (NOT inside the funnel, per the campaign brief). The
 * visual structure mirrors the prototype's `Sankey` component so the
 * pre-baked `.sankey-wrap` / `.sankey-key` CSS in dashboard.css picks
 * it up unchanged.
 */
import { formatNumber } from '../../utils/format';

export interface FunnelProps {
  reached: number;
  rsvp_yes: number;
  attended: number;
  walk_in: number;
  /** Height of the SVG; default mirrors the prototype's 320px. */
  height?: number;
}

const W = 920;
const COL_W = 130;
const PAD = { l: 8, r: 8, t: 18, b: 28 } as const;

function pct(n: number, d: number): string {
  return d > 0 ? `${Math.round((n / d) * 100)}%` : '—';
}

/**
 * Smooth filled Sankey-style flow between two y-bands at x0 and x1.
 */
function flow(
  x0: number,
  y0a: number,
  y0b: number,
  x1: number,
  y1a: number,
  y1b: number,
): string {
  const mid = (x0 + x1) / 2;
  return [
    `M${x0},${y0a}`,
    `C${mid},${y0a} ${mid},${y1a} ${x1},${y1a}`,
    `L${x1},${y1b}`,
    `C${mid},${y1b} ${mid},${y0b} ${x0},${y0b}`,
    'Z',
  ].join(' ');
}

export function Funnel({
  reached,
  rsvp_yes,
  attended,
  walk_in,
  height = 320,
}: FunnelProps) {
  // Walk-ins are a SUBSET of reached (rule #2): they are reached rows where
  // attended=true and rsvp≠"yes". So the four buckets that partition reached are:
  //
  //   rsvpYesAttended   — rsvp=yes  AND attended       =  attended - walk_in
  //   rsvpYesNoShow     — rsvp=yes  AND not attended   =  rsvp_yes - rsvpYesAttended
  //   walk_in           — rsvp≠yes  AND attended
  //   otherNoShow       — rsvp≠yes  AND not attended   =  the remainder
  //
  // Sum of the four = reached. Total attended = rsvpYesAttended + walk_in.
  const rsvpYesAttended = Math.max(0, attended - walk_in);
  const rsvpYesNoShow = Math.max(0, rsvp_yes - rsvpYesAttended);
  const noResponse = Math.max(0, reached - rsvp_yes);             // rsvp≠yes pool
  const otherNoShow = Math.max(0, noResponse - walk_in);          // rsvp≠yes AND didn't attend
  const totalAttended = rsvpYesAttended + walk_in;
  const totalNoShow = rsvpYesNoShow + otherNoShow;                // = reached - totalAttended

  const h = height;
  const innerW = W - PAD.l - PAD.r;
  const innerH = h - PAD.t - PAD.b;

  const gap = (innerW - COL_W * 3) / 2;
  const cols = [PAD.l, PAD.l + COL_W + gap, PAD.l + 2 * (COL_W + gap)] as const;

  // Total vertical units = reached. Walk-ins are inside reached, not added on top.
  const totalUnits = Math.max(1, reached);
  const yScale = innerH / totalUnits;

  // Stage 1 (Reached): RSVP-yes (top) + no-response (bottom). Total = reached.
  const s1YesH = rsvp_yes * yScale;
  const s1YesY = PAD.t;
  const s1NoH = noResponse * yScale;
  const s1NoY = PAD.t + s1YesH;

  // Stage 2 splits the no-response band into the "other no-show" portion
  // (which will flow to Stage 3 didn't-attend) and the walk-in portion
  // (which will flow to Stage 3 attended). Walk-in is INSIDE no-response,
  // not added on top — its band sits at the bottom of the no-response band.
  const s2YesH = rsvp_yes * yScale;
  const s2YesY = PAD.t;
  const s2OtherNoH = otherNoShow * yScale;
  const s2OtherNoY = PAD.t + s2YesH;
  const s2WalkH = walk_in * yScale;
  const s2WalkY = s2OtherNoY + s2OtherNoH;

  // Stage 3 (Attended): attended top, did-not-attend bottom. Sub-bands so
  // the yes-attended portion sits above the walk-in portion, mirroring the
  // colour of their source flows.
  const yesAttH = rsvpYesAttended * yScale;
  const yesNoH = rsvpYesNoShow * yScale;
  const s3AttH = totalAttended * yScale;
  const s3AttY = PAD.t;
  const s3NoH = totalNoShow * yScale;
  const s3NoY = PAD.t + s3AttH;

  // Stage 1 → 2 flows.
  const pYes = flow(
    cols[0] + COL_W, s1YesY, s1YesY + s1YesH,
    cols[1],         s2YesY, s2YesY + s2YesH,
  );
  // Split the no-response inflow into the two destinations at stage 2.
  const pNoResp = flow(
    cols[0] + COL_W, s1NoY, s1NoY + s2OtherNoH,
    cols[1],         s2OtherNoY, s2OtherNoY + s2OtherNoH,
  );
  const pNoRespToWalk = flow(
    cols[0] + COL_W, s1NoY + s2OtherNoH, s1NoY + s2OtherNoH + s2WalkH,
    cols[1],         s2WalkY,            s2WalkY + s2WalkH,
  );

  // Stage 2 → 3 flows.
  // yes-attended: top of stage 2 yes  →  top of stage 3 attended.
  const pYesAtt = flow(
    cols[1] + COL_W, s2YesY,           s2YesY + yesAttH,
    cols[2],         s3AttY,           s3AttY + yesAttH,
  );
  // yes-no-show: bottom of stage 2 yes  →  top of stage 3 didn't-attend.
  const pYesNo = flow(
    cols[1] + COL_W, s2YesY + yesAttH, s2YesY + yesAttH + yesNoH,
    cols[2],         s3NoY,            s3NoY + yesNoH,
  );
  // other-no-show: stage 2 other-no  →  bottom of stage 3 didn't-attend.
  const pNoShow = flow(
    cols[1] + COL_W, s2OtherNoY,       s2OtherNoY + s2OtherNoH,
    cols[2],         s3NoY + yesNoH,   s3NoY + yesNoH + s2OtherNoH,
  );
  // walk-in: stage 2 walk-in band  →  bottom of stage 3 attended.
  const pWalk = flow(
    cols[1] + COL_W, s2WalkY,          s2WalkY + s2WalkH,
    cols[2],         s3AttY + yesAttH, s3AttY + yesAttH + s2WalkH,
  );

  return (
    <div className="sankey-wrap">
      <div className="sankey-key">
        <span><span className="swatch" style={{ background: 'var(--ink)' }} />פניות ורישומים</span>
        <span><span className="swatch" style={{ background: 'var(--c-navy)' }} />אישרו הגעה</span>
        <span><span className="swatch" style={{ background: 'var(--accent)' }} />הגעה בפועל</span>
        <span><span className="swatch" style={{ background: 'var(--ink-4)', opacity: 0.6 }} />לא הגיעו / ללא תגובה</span>
        <span><span className="swatch" style={{ background: 'var(--c-yellow)' }} />walk-in (ללא הרשמה)</span>
      </div>

      <svg
        viewBox={`0 0 ${W} ${h}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ direction: 'ltr' }}
        role="img"
        aria-label="משפך הגיוס — פניות, אישורי הגעה, הגעה בפועל"
      >
        {/* Flows behind, nodes in front. */}
        <path d={pYes}          fill="var(--c-navy)"   opacity="0.22" />
        <path d={pNoResp}       fill="var(--ink-4)"    opacity="0.18" />
        <path d={pNoRespToWalk} fill="var(--c-yellow)" opacity="0.40" />
        <path d={pYesAtt}       fill="var(--accent)"   opacity="0.32" />
        <path d={pYesNo}        fill="var(--c-navy)"   opacity="0.14" />
        <path d={pNoShow}       fill="var(--ink-4)"    opacity="0.15" />
        <path d={pWalk}         fill="var(--c-yellow)" opacity="0.55" />

        {/* Stage 1 nodes (Reached split into yes + no-response). */}
        <rect x={cols[0]} y={s1YesY} width={COL_W} height={s1YesH} fill="var(--ink)" />
        <rect x={cols[0]} y={s1NoY}  width={COL_W} height={s1NoH}  fill="var(--ink-4)" opacity="0.35" />

        {/* Stage 2 nodes — yes-band, other-no-show, then the walk-in highlight
            sitting at the bottom of the no-response band. The three together
            sum to `reached`. */}
        <rect x={cols[1]} y={s2YesY}     width={COL_W} height={s2YesH}     fill="var(--c-navy)" />
        <rect x={cols[1]} y={s2OtherNoY} width={COL_W} height={s2OtherNoH} fill="var(--ink-4)" opacity="0.35" />
        <rect x={cols[1]} y={s2WalkY}    width={COL_W} height={s2WalkH}    fill="var(--c-yellow)" />

        {/* Stage 3 nodes — each total block is split into its source sub-bands
            so the breakdown is visible:
              attended  = (yes-approved & came: navy) + (walk-in: yellow)
              not-att.  = (yes-approved & no-show: navy soft) + (no-response: gray)
        */}
        {/* Attended: yes-portion (top) + walk-in (bottom). */}
        <rect x={cols[2]} y={s3AttY}             width={COL_W} height={yesAttH}  fill="var(--c-navy)" />
        <rect x={cols[2]} y={s3AttY + yesAttH}   width={COL_W} height={s2WalkH}  fill="var(--c-yellow)" />
        {/* Did-not-attend: yes-no-show (top) + other-no-response (bottom). */}
        <rect x={cols[2]} y={s3NoY}              width={COL_W} height={yesNoH}      fill="var(--c-navy)" opacity="0.35" />
        <rect x={cols[2]} y={s3NoY + yesNoH}     width={COL_W} height={s2OtherNoH}  fill="var(--ink-4)" opacity="0.35" />
        {/* Thin teal stripe on the left edge of the attended block, so the
            "actually attended" identity stays readable against the navy. */}
        <rect x={cols[2]} y={s3AttY} width={3} height={s3AttH} fill="var(--accent)" />

        {/* Column labels (Hebrew, RTL-aware via textAnchor=middle). */}
        <g style={{ fontFamily: 'var(--font-sans)', fontSize: 11, fill: 'var(--ink-3)' }}>
          <text x={cols[0] + COL_W / 2} y={h - 12} textAnchor="middle" letterSpacing="0.06em" fontWeight={600}>
            פניות ורישומים
          </text>
          <text x={cols[1] + COL_W / 2} y={h - 12} textAnchor="middle" letterSpacing="0.06em" fontWeight={600}>
            אישרו הגעה · walk-in
          </text>
          <text x={cols[2] + COL_W / 2} y={h - 12} textAnchor="middle" letterSpacing="0.06em" fontWeight={600}>
            הגיעו לאירוע
          </text>
        </g>

        {/* In-node numbers. */}
        <g style={{ fontFamily: 'var(--font-serif)', fontSize: 16, fontWeight: 500 }}>
          {/* Stage 1 */}
          {s1YesH > 18 && (
            <>
              <text x={cols[0] + 10} y={s1YesY + 22} fill="var(--paper)">{formatNumber(rsvp_yes)}</text>
              <text x={cols[0] + 10} y={s1YesY + 38} fill="var(--paper)" fontFamily="var(--font-sans)" fontSize={10} letterSpacing="0.06em" fontWeight={600}>
                אישרו הגעה
              </text>
            </>
          )}
          {s1NoH > 18 && (
            <>
              <text x={cols[0] + 10} y={s1NoY + 22} fill="var(--ink)">{formatNumber(noResponse)}</text>
              <text x={cols[0] + 10} y={s1NoY + 38} fill="var(--ink-2)" fontFamily="var(--font-sans)" fontSize={10} letterSpacing="0.06em" fontWeight={600}>
                ללא תגובה
              </text>
            </>
          )}
          {/* Stage 2 */}
          {s2YesH > 18 && (
            <>
              <text x={cols[1] + 10} y={s2YesY + 22} fill="var(--paper)">{formatNumber(rsvp_yes)}</text>
              <text x={cols[1] + 10} y={s2YesY + 38} fill="var(--paper)" fontFamily="var(--font-sans)" fontSize={10} letterSpacing="0.06em" fontWeight={600}>
                התחייבו
              </text>
            </>
          )}
          {s2WalkH > 16 && (
            <>
              <text x={cols[1] + 10} y={s2WalkY + 18} fill="var(--ink)">{formatNumber(walk_in)}</text>
              <text x={cols[1] + 10} y={s2WalkY + 32} fill="var(--ink-2)" fontFamily="var(--font-sans)" fontSize={10} letterSpacing="0.06em" fontWeight={600}>
                walk-in
              </text>
            </>
          )}
          {/* Stage 3 — each sub-band shows its own breakdown inside, totals
              live in the side-callout column to the right of the SVG. */}
          {yesAttH > 18 && (
            <>
              <text x={cols[2] + 10} y={s3AttY + Math.min(yesAttH / 2 + 4, yesAttH - 6)} fill="var(--paper)" fontSize={14}>
                {formatNumber(rsvpYesAttended)}
              </text>
              {yesAttH > 36 && (
                <text x={cols[2] + 10} y={s3AttY + yesAttH / 2 + 18} fill="var(--paper)" fontFamily="var(--font-sans)" fontSize={9} letterSpacing="0.04em" fontWeight={600}>
                  אישרו ובאו
                </text>
              )}
            </>
          )}
          {s2WalkH > 14 && (
            <>
              <text x={cols[2] + 10} y={s3AttY + yesAttH + Math.min(s2WalkH / 2 + 4, s2WalkH - 4)} fill="var(--ink)" fontSize={13}>
                {formatNumber(walk_in)}
              </text>
              {s2WalkH > 30 && (
                <text x={cols[2] + 10} y={s3AttY + yesAttH + s2WalkH / 2 + 16} fill="var(--ink-2)" fontFamily="var(--font-sans)" fontSize={9} letterSpacing="0.04em" fontWeight={600}>
                  walk-in
                </text>
              )}
            </>
          )}
          {yesNoH > 14 && (
            <>
              <text x={cols[2] + 10} y={s3NoY + Math.min(yesNoH / 2 + 4, yesNoH - 4)} fill="var(--ink)" fontSize={13}>
                {formatNumber(rsvpYesNoShow)}
              </text>
              {yesNoH > 30 && (
                <text x={cols[2] + 10} y={s3NoY + yesNoH / 2 + 16} fill="var(--ink-2)" fontFamily="var(--font-sans)" fontSize={9} letterSpacing="0.04em" fontWeight={600}>
                  אישרו ולא באו
                </text>
              )}
            </>
          )}
          {s2OtherNoH > 18 && (
            <>
              <text x={cols[2] + 10} y={s3NoY + yesNoH + Math.min(s2OtherNoH / 2 + 4, s2OtherNoH - 6)} fill="var(--ink)" fontSize={14}>
                {formatNumber(otherNoShow)}
              </text>
              {s2OtherNoH > 36 && (
                <text x={cols[2] + 10} y={s3NoY + yesNoH + s2OtherNoH / 2 + 18} fill="var(--ink-2)" fontFamily="var(--font-sans)" fontSize={9} letterSpacing="0.04em" fontWeight={600}>
                  לא אישרו
                </text>
              )}
            </>
          )}
          {/* Side totals — placed to the left of stage 3 blocks (visual LEFT
              edge = end of RTL reading) so they don't fight with the sub-labels. */}
          <text x={cols[2] - 12} y={s3AttY + 14} textAnchor="end" fill="var(--ink)" fontSize={11} fontFamily="var(--font-sans)" fontWeight={600}>
            סה״כ הגיעו {formatNumber(totalAttended)}
          </text>
          <text x={cols[2] - 12} y={s3NoY + 14} textAnchor="end" fill="var(--ink-2)" fontSize={11} fontFamily="var(--font-sans)" fontWeight={600}>
            סה״כ לא הגיעו {formatNumber(totalNoShow)}
          </text>
        </g>

        {/* Conversion callouts between stages. */}
        <g style={{ fontFamily: 'var(--font-mono)', fontSize: 10, fill: 'var(--ink-3)' }}>
          <text x={(cols[0] + COL_W + cols[1]) / 2} y={PAD.t - 4} textAnchor="middle">
            {pct(rsvp_yes, reached)} מהפניות אישרו הגעה
          </text>
          <text x={(cols[1] + COL_W + cols[2]) / 2} y={PAD.t - 4} textAnchor="middle">
            {pct(rsvpYesAttended, rsvp_yes)} מהמאשרים הגיעו · {pct(walk_in, Math.max(1, totalAttended))} מהמגיעים — walk-in
          </text>
        </g>
      </svg>
    </div>
  );
}
